import { useState, useContext, useCallback, useEffect } from "react";
import {
  Text,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Box,
  useColorModeValue as mode,
  InputGroup,
  InputRightElement,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SimpleGrid,
  Tooltip,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import shortid from "shortid";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";

// Components
import Separator from "./separator";

// Types
import type { Carousel } from "types/carousel";

// Utils
import { getToken } from "../../firebaseClient";
import { useSession } from "next-auth/react";
import { MainContext } from "context/mainContext";
import { getCreditBack, useCredit } from "controllers/subscription";
import { getAccount } from "utils/sessionHelper";
import { uploadOnS3 } from "utils/uploadUtils";
import { sanitizeInput } from "utils/sanitizationHelpers";

const baseUrl = "https://us-central1-ez4cast.cloudfunctions.net";
const BUCKET_NAME = process.env.NEXT_PUBLIC_PROJECT_BUCKET;

const aspectRatios = ["original", "16:9", "4:3", "1:1"];
const imagePositions = ["top", "bottom"];
const imageStyles = ["none", "rounded", "circle"];
const imageAlignments = ["left", "center", "right"];

const generateAISingle = async (
  prevCarousels,
  currentSlide,
  type,
  session,
  useBestAi
) => {
  const token = await getToken(session, "aiCarouselGenerator");
  const userDescription = getAccount(session)?.description;
  return await fetch(`${baseUrl}/linkedinCarousell-generateaiSingle`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      tokenuserid: session.user.uid,
    },
    body: JSON.stringify({
      input: JSON.stringify(prevCarousels),
      description: userDescription,
      currentSlide: JSON.stringify({ ...currentSlide, [type]: "" }),
      type,
      useBestAi,
      isInfluentLeader: true,
    }),
  });
};

export default function Editor({
  content: {
    id,
    title,
    titleActive,
    text,
    textActive,
    type,
    image,
    imagePosition = "top",
    imageAspectRatio = "original",
    imageWidth = 30,
    imageStyle = "rounded",
    imageAlign = "left",
  },
  prevCarousels,
  onChange,
}: {
  content: Carousel;
  prevCarousels: Carousel[];
  onChange: (key: string, value: string | boolean | number) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) {
        return;
      }
      setIsUploading(true);
      let shortId = shortid.generate().replace("-", "_");
      const fileName = `carousel-${id}-${shortId}`;
      const fileExtension = acceptedFiles[0].name.split(".").pop();
      const params = {
        Bucket: BUCKET_NAME,
        Key: `userUpload/${fileName}.${fileExtension}`,
        Body: acceptedFiles[0],
        ACL: "public-read",
      };
      const uploadedFile = (await uploadOnS3(params)) as string;
      onChange("image", uploadedFile);
      setIsUploading(false);
    },
    [onChange, setIsUploading]
  );
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: { "image/*": [] },
      maxFiles: 1,
      multiple: false,
      maxSize: 5 * 1024 * 1024,
      onDrop,
    });
  const [isLoading, setIsLoading] = useState({
    title: false,
    text: false,
  });
  const { data: session } = useSession() ?? {};
  const mainContext = useContext(MainContext);
  const account = getAccount(session);
  const isStarterPlan = account?.subscription?.plan === "start";

  const handleGenerateWithAI = async (fieldType) => {
    setIsLoading((prevLoading) => ({ ...prevLoading, [fieldType]: true }));
    let useBestAi = await useCredit(
      session,
      mainContext,
      "creditsBestai",
      false
    );
    try {
      const res = await generateAISingle(
        prevCarousels,
        {
          type,
          title,
          text,
          subtitle: "",
        },
        fieldType,
        session,
        useBestAi
      );
      let data = await res.json();
      if (data?.success === 1 && data?.carousel) {
        onChange(fieldType, data.carousel[fieldType]);
      } else if (data.success === 0 && data.error) {
        toast.error(data.error);
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error(
        "Something went wrong while generating the carousel. Please try again."
      );
      getCreditBack(session, "creditsBestai");
    }

    setIsLoading((prevLoading) => ({ ...prevLoading, [fieldType]: false }));
  };

  const isCoverSlide = type === "introduction";
  useEffect(() => {
    if (fileRejections.length > 0) {
      if (
        fileRejections.length > 0 &&
        fileRejections[0].errors[0].code === "file-too-large"
      ) {
        toast.error("File size should be less than 5MB");
      }
      if (
        fileRejections.length > 0 &&
        fileRejections[0].errors[0].code === "too-many-files"
      ) {
        toast.error("You can upload only one file");
      }
    }
  }, [fileRejections]);
  return (
    <VStack align="start" spacing={4}>
      <VStack spacing={2} align="start">
        <Text as="h3" textStyle="h3.bold.standard">
          Content Edit
        </Text>
        <Box
          w="30%"
          h="2px"
          bg={mode("text.lightMode.standard", "text.darkMode.standard")}
        />
      </VStack>
      <VStack spacing={4} w="100%">
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="title" m={0}>
              Title
            </FormLabel>
            <Switch
              size="sm"
              isChecked={titleActive}
              onChange={(e) => onChange("titleActive", e.target.checked)}
            />
          </HStack>
          {titleActive &&
            (isCoverSlide ? (
              <Input
                pr="4.5rem"
                type="text"
                value={title}
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value);
                  onChange("title", sanitizedValue);
                }}
              />
            ) : (
              <InputGroup>
                <Input
                  pr="4.5rem"
                  type="text"
                  value={title}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeInput(e.target.value);
                    onChange("title", sanitizedValue);
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Tooltip
                    isDisabled={!isStarterPlan}
                    label="Upgrade to Standard or Pro plan to use AI features"
                    placement="top"
                  >
                    <Box>
                      <Button
                        size="sm"
                        variant="secondaryAI"
                        onClick={() => handleGenerateWithAI("title")}
                        isLoading={isLoading.title}
                        isDisabled={isStarterPlan}
                      >
                        ⚡️ AI
                      </Button>
                    </Box>
                  </Tooltip>
                </InputRightElement>
              </InputGroup>
            ))}
        </FormControl>
        <Separator />
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="text" m={0}>
              {isCoverSlide ? "Subtitle" : "Text"}
            </FormLabel>
            <Switch
              size="sm"
              isChecked={textActive}
              onChange={(e) => {
                onChange("textActive", e.target.checked);
              }}
            />
          </HStack>
          {textActive &&
            (isCoverSlide ? (
              <Textarea
                value={text}
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value);
                  onChange("text", sanitizedValue);
                }}
              />
            ) : (
              <InputGroup>
                <Textarea
                  pr="4.5rem"
                  value={text}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeInput(e.target.value);
                    onChange("text", sanitizedValue);
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Tooltip
                    isDisabled={!isStarterPlan}
                    label="Upgrade to Standard or Pro plan to use AI features"
                    placement="top"
                  >
                    <Box>
                      <Button
                        size="sm"
                        variant="secondaryAI"
                        onClick={() => handleGenerateWithAI("text")}
                        isLoading={isLoading.text}
                        isDisabled={isStarterPlan}
                      >
                        ⚡️ AI
                      </Button>
                    </Box>
                  </Tooltip>
                </InputRightElement>
              </InputGroup>
            ))}
        </FormControl>
        <Separator />
        <FormControl>
          <VStack justifyContent="space-between" align={"left"} mb={1}>
            <FormLabel htmlFor="text" m={0}>
              Image
            </FormLabel>
            {Boolean(image) ? (
              <Box w={100} h={100} position="relative">
                <Box w="100%" h="100%" overflow="hidden">
                  <Image
                    src={image}
                    alt="carousel"
                    objectFit="cover"
                    objectPosition="center"
                    w="100%"
                    h="100%"
                    borderRadius={6}
                  />
                </Box>
                <IconButton
                  p={1}
                  size="xs"
                  aria-label="remove"
                  icon={<CloseIcon w={2} h={2} />}
                  variant="secondary"
                  onClick={() => onChange("image", "")}
                  position="absolute"
                  top={-2}
                  right={-2}
                  boxShadow="sm"
                />
              </Box>
            ) : (
              <Box
                {...(!isUploading && getRootProps())}
                borderWidth={3}
                borderStyle="dashed"
                borderRadius={5}
                p={6}
                textAlign="center"
                cursor="pointer"
              >
                {isUploading ? (
                  <Text
                    as="p"
                    color={mode("text.lightMode.light", "text.darkMode.light")}
                  >
                    Uploading...
                  </Text>
                ) : (
                  <>
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <Text
                        as="p"
                        color={mode(
                          "text.lightMode.light",
                          "text.darkMode.light"
                        )}
                      >
                        Drop the image here ...
                      </Text>
                    ) : (
                      <Text
                        as="p"
                        color={mode(
                          "text.lightMode.light",
                          "text.darkMode.light"
                        )}
                      >
                        Drag & Drop image here, or click to select image
                      </Text>
                    )}
                  </>
                )}
              </Box>
            )}
            {Boolean(image) && (
              <VStack align="start">
                <FormLabel htmlFor="text" m={0} fontSize="sm">
                  Options
                </FormLabel>
                <SimpleGrid columns={4} gap={2}>
                  <Menu autoSelect={false}>
                    <MenuButton
                      size="xs"
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      textTransform="capitalize"
                    >
                      Ratio: {imageAspectRatio}
                    </MenuButton>
                    <MenuList minWidth="initial">
                      {aspectRatios.map((ratio) => (
                        <MenuItem
                          key={ratio}
                          value={ratio}
                          textTransform="capitalize"
                          onClick={() =>
                            onChange(
                              "imageAspectRatio",
                              ratio.replace(":", " / ")
                            )
                          }
                        >
                          {ratio}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu autoSelect={false}>
                    <MenuButton
                      size="xs"
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      textTransform="capitalize"
                    >
                      Position: {imagePosition}
                    </MenuButton>
                    <MenuList minW="initial">
                      {imagePositions.map((position) => (
                        <MenuItem
                          key={position}
                          value={position}
                          textTransform="capitalize"
                          onClick={() => onChange("imagePosition", position)}
                        >
                          {position}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu autoSelect={false}>
                    <MenuButton
                      size="xs"
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      textTransform="capitalize"
                    >
                      Style: {imageStyle}
                    </MenuButton>
                    <MenuList minW="initial">
                      {imageStyles.map((style) => (
                        <MenuItem
                          key={style}
                          value={style}
                          textTransform="capitalize"
                          onClick={() => onChange("imageStyle", style)}
                        >
                          {style}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu autoSelect={false}>
                    <MenuButton
                      size="xs"
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      textTransform="capitalize"
                    >
                      Align: {imageAlign}
                    </MenuButton>
                    <MenuList minW="initial">
                      {imageAlignments.map((align) => (
                        <MenuItem
                          key={align}
                          value={align}
                          textTransform="capitalize"
                          onClick={() => onChange("imageAlign", align)}
                        >
                          {align}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </SimpleGrid>
                <HStack
                  w="100%"
                  bg={mode("gray.100", "whiteAlpha.200")}
                  py={1}
                  px={2}
                  borderRadius="md"
                >
                  <Text
                    fontSize="xs"
                    m={0}
                    whiteSpace="nowrap"
                    fontWeight="bold"
                  >
                    Width: {imageWidth}%
                  </Text>
                  <Slider
                    aria-label="image-width-slider"
                    colorScheme="blue"
                    min={5}
                    step={5}
                    value={imageWidth}
                    onChange={(value) => onChange("imageWidth", value)}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </HStack>
              </VStack>
            )}
          </VStack>
        </FormControl>
      </VStack>
    </VStack>
  );
}
